/* @flow */

/**
 * @description wrapper for FETCH API
 * @module utils/fetch
 * @exports utils/fetch
 * @requires utils/coreutils
 */

import { noop, isObject, isString, extend, buildUrl } from './coreutils';
import { parseJSON } from './browserutil';
import { logError } from './logger';

/**
 * checks status of the response
 *
 * @param {Response} response - Fetch request's response
 * @returns {Promise<any>} - Resolve/Rejected Promise
 */
function status(response: Response): Promise<Response> {
  if (response.ok) {
    return Promise.resolve(response);
  }
  return Promise.reject(new Error(response.statusText));
}

/**
 * Extracts json data from fetch response
 *
 * @param {Response} resp - Fetch request's response
 * @returns {Promise<*>} - Resolve/Rejected Promise
 */
function json(resp: Response): Promise<{}> {
  // no content case
  if (resp.status === 204) {
    return Promise.resolve({});
  }
  const contentType = resp.headers.get('content-type') || '';
  if (
    contentType.length > 0 &&
    (contentType.includes('application/json') ||
      contentType.includes('text/html')) // RT#493270
  ) {
    return resp.json();
  }
  if (contentType.length > 0 && contentType.includes('text/plain')) {
    // parse using JSON parser
    return resp.text().then(v => parseJSON(window, v));
  }
  if (
    contentType.length > 0 &&
    contentType.includes('application/javascript')
  ) {
    // Assuming some web server may return JSON as javascript content type
    try {
      return resp.text().then(v => parseJSON(window, v));
    } catch (error) {
      // ignore error
      logError(`Error parsing JSON response - ${JSON.stringify(error)}`);
    }
  }
  return Promise.reject(
    new Error(`Invalid content type: ${contentType || 'empty'}`)
  );
}

/**
 * utility func to check fetch support
 *
 * @returns {boolean} - fetch support status
 */
export function isFetchSupported(): boolean {
  return 'fetch' in window;
}

// flowlint unclear-type:off

/**
 * utility function for fetch
 *
 * @param {string} url - URL
 * @param {module:jsonp.jsonpConfig} o - JSONP Config object
 * @param {Request} req - Request object
 * @returns {Promise<object>} - Returns promise
 */
export function fetchJSON(
  url: string,
  o: jsonpConfig,
  req?: any
): Promise<any> {
  // flowlint unclear-type:error
  if (!isFetchSupported()) {
    return Promise.reject('fetch not supported');
  }

  // default option if nothing provided. Caller can override
  const _defOptions: jsonpConfig = {
    cache: false,
    data: {},
    jsonpCallback: noop,
    timeout: 300,
    logPrefix: '',
    context: window
  };

  // invalid input
  if (!isObject(o) || !isString(url) || !url.length) {
    return Promise.reject('Invalid args!');
  }

  // combined options are stored here
  // intentionally not doing deep merge since we have window object in defOptions
  const opt: jsonpConfig = extend({}, _defOptions, o);
  const req2: anyObject = req || ({} as anyObject);
  // serialize data for adding to query param
  const url2 = buildUrl(url, opt.data);

  // porting cache from options to Request object
  if (opt.cache === false) {
    req2.cache = 'no-store';
  }

  return new Promise((resolve, reject) => {
    let loadTimer: ?TimeoutID = setTimeout(
      () => reject(`${opt.logPrefix} fetch failed: timeout`),
      opt.timeout
    );

    fetch(url2, req2)
      .then(status)
      .then(json)
      .then(
        data => {
          if (loadTimer) {
            clearTimeout(loadTimer);
            loadTimer = null;
          }
          if (opt.jsonpCallback) {
            opt.jsonpCallback.call(opt.context, data);
          }
          resolve(data);
          return data;
        },
        error => reject(`${opt.logPrefix} ${error.message}`)
      );
  });
}
